import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Hologram images rotated inside the hero (country/team imagery)
// Orden: Chile, México, Colombia, Argentina, España, Perú
const HERO_IMAGES = [
  '/hero-images/cl.png',
  '/hero-images/mx.png',
  '/hero-images/cb.png',
  '/hero-images/ar.png',
  '/hero-images/esp.png',
  '/hero-images/pe.png',
];

// Soft edge mask: feathers the bottom + sides so the harsh cut-out
// of the subject blends into the hero background instead of looking
// like a sticker. Combined as -webkit-mask-image / mask-image.
const SOFT_EDGE_MASK =
  'linear-gradient(to bottom, black 0%, black 70%, rgba(0,0,0,0.85) 85%, rgba(0,0,0,0) 100%), ' +
  'linear-gradient(to right, rgba(0,0,0,0) 0%, black 8%, black 92%, rgba(0,0,0,0) 100%)';
const SOFT_EDGE_MASK_STYLE = {
  WebkitMaskImage: SOFT_EDGE_MASK,
  maskImage: SOFT_EDGE_MASK,
  WebkitMaskComposite: 'source-in',
  maskComposite: 'intersect',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
  WebkitMaskSize: '100% 100%',
  maskSize: '100% 100%',
} as const;

// Glitch keyframes as CSS
const glitchStyles = `
  @keyframes glitch-skew {
    0% { transform: skewX(0deg); }
    20% { transform: skewX(-2deg); }
    40% { transform: skewX(1deg); }
    60% { transform: skewX(-1deg); }
    80% { transform: skewX(0.5deg); }
    100% { transform: skewX(0deg); }
  }
  
  @keyframes glitch-jitter {
    0%, 100% { transform: translate(0, 0); }
    10% { transform: translate(-2px, 1px); }
    20% { transform: translate(2px, -1px); }
    30% { transform: translate(-1px, 2px); }
    40% { transform: translate(1px, -2px); }
    50% { transform: translate(-2px, -1px); }
    60% { transform: translate(2px, 1px); }
    70% { transform: translate(-1px, -2px); }
    80% { transform: translate(1px, 2px); }
    90% { transform: translate(-2px, 0); }
  }
  
  @keyframes scanline {
    0% { top: -100%; }
    100% { top: 100%; }
  }
  
  @keyframes flicker {
    0%, 100% { opacity: 1; }
    5% { opacity: 0.9; }
    10% { opacity: 1; }
    15% { opacity: 0.85; }
    20% { opacity: 1; }
    25% { opacity: 0.95; }
    30% { opacity: 1; }
    50% { opacity: 0.9; }
    55% { opacity: 1; }
    60% { opacity: 0.85; }
    65% { opacity: 1; }
  }
  
  @keyframes chromatic {
    0%, 100% { 
      filter: drop-shadow(2px 0 0 rgba(0, 255, 255, 0.3)) drop-shadow(-2px 0 0 rgba(255, 0, 128, 0.3));
    }
    25% { 
      filter: drop-shadow(-2px 0 0 rgba(0, 255, 255, 0.4)) drop-shadow(2px 0 0 rgba(255, 0, 128, 0.4));
    }
    50% { 
      filter: drop-shadow(1px 0 0 rgba(0, 255, 255, 0.2)) drop-shadow(-1px 0 0 rgba(255, 0, 128, 0.2));
    }
    75% { 
      filter: drop-shadow(-1px 0 0 rgba(0, 255, 255, 0.5)) drop-shadow(1px 0 0 rgba(255, 0, 128, 0.5));
    }
  }
  
  @keyframes digital-noise {
    0%, 100% { clip-path: inset(0 0 0 0); }
    10% { clip-path: inset(5% 0 10% 0); }
    20% { clip-path: inset(15% 0 5% 0); }
    30% { clip-path: inset(10% 0 15% 0); }
    40% { clip-path: inset(25% 0 5% 0); }
    50% { clip-path: inset(0 0 30% 0); }
    60% { clip-path: inset(20% 0 10% 0); }
    70% { clip-path: inset(5% 0 25% 0); }
    80% { clip-path: inset(10% 0 5% 0); }
    90% { clip-path: inset(30% 0 5% 0); }
  }
`;

interface IniestaGlitchProps {
  className?: string;
  /**
   * @deprecated Kept for backward compatibility; no longer used after switching
   * to the hero-images rotation.
   */
  showAppVersion?: boolean;
}

export function IniestaGlitch({ className = '' }: IniestaGlitchProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isGlitching, setIsGlitching] = useState(false);

  const images = useMemo(() => HERO_IMAGES, []);

  // Auto-rotate images with glitch effect
  useEffect(() => {
    const rotateInterval = setInterval(() => {
      // Trigger glitch
      setIsGlitching(true);
      
      // After glitch, change image
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
        
        // End glitch after transition
        setTimeout(() => {
          setIsGlitching(false);
        }, 200);
      }, 150);
    }, 5000);

    return () => clearInterval(rotateInterval);
  }, [images.length]);

  // Random micro-glitches
  useEffect(() => {
    const microGlitchInterval = setInterval(() => {
      if (Math.random() > 0.7) {
        setIsGlitching(true);
        setTimeout(() => setIsGlitching(false), 100 + Math.random() * 150);
      }
    }, 2000);

    return () => clearInterval(microGlitchInterval);
  }, []);

  return (
    <>
      <style>{glitchStyles}</style>
      <div className={`relative ${className}`}>
        {/* Main image container with glitch effects */}
        <div 
          className="relative w-full h-full"
          style={{
            animation: isGlitching 
              ? 'glitch-skew 0.3s ease-in-out, flicker 0.15s ease-in-out' 
              : 'flicker 8s ease-in-out infinite',
          }}
        >
          {/* Chromatic aberration layer - Red */}
          <div 
            className="absolute inset-0 mix-blend-screen opacity-60"
            style={{
              transform: isGlitching ? 'translateX(4px)' : 'translateX(2px)',
              filter: 'url(#red-channel)',
              transition: 'transform 0.1s ease-out',
            }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={`red-${currentIndex}`}
                src={images[currentIndex]}
                alt=""
                className="w-full h-full object-contain object-bottom"
                style={{
                  filter: 'grayscale(100%) brightness(1.2) blur(0.3px)',
                  mixBlendMode: 'multiply',
                  ...SOFT_EDGE_MASK_STYLE,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.3 }}
                exit={{ opacity: 0 }}
              />
            </AnimatePresence>
          </div>

          {/* Chromatic aberration layer - Cyan */}
          <div 
            className="absolute inset-0 mix-blend-screen opacity-60"
            style={{
              transform: isGlitching ? 'translateX(-4px)' : 'translateX(-2px)',
              transition: 'transform 0.1s ease-out',
            }}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={`cyan-${currentIndex}`}
                src={images[currentIndex]}
                alt=""
                className="w-full h-full object-contain object-bottom"
                style={{
                  filter: 'grayscale(100%) brightness(1.2) hue-rotate(180deg) blur(0.3px)',
                  mixBlendMode: 'multiply',
                  ...SOFT_EDGE_MASK_STYLE,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.3 }}
                exit={{ opacity: 0 }}
              />
            </AnimatePresence>
          </div>

          {/* Main image */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                x: isGlitching ? [0, -3, 3, -1, 1, 0] : 0,
              }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ 
                opacity: { duration: 0.3 },
                scale: { duration: 0.5 },
                x: { duration: 0.2 },
              }}
              className="absolute inset-0 z-10 flex items-end justify-center"
              style={{
                animation: isGlitching ? 'glitch-jitter 0.2s ease-in-out' : undefined,
              }}
            >
              <img
                src={images[currentIndex]}
                alt="BTalentStar - Hero showcase"
                className="block w-full h-full object-contain object-bottom drop-shadow-2xl"
                style={{
                  animation: 'chromatic 4s ease-in-out infinite',
                  filter: isGlitching
                    ? 'contrast(1.08) saturate(1.25) brightness(1.02) blur(0.2px) drop-shadow(0 0 24px rgba(255, 215, 0, 0.45))'
                    : 'contrast(1.05) saturate(1.15) brightness(1.02) blur(0.2px) drop-shadow(0 0 30px rgba(255, 215, 0, 0.22))',
                  imageRendering: 'auto',
                  ...SOFT_EDGE_MASK_STYLE,
                }}
              />
            </motion.div>
          </AnimatePresence>

          {/* Scanline effect */}
          <div 
            className="absolute inset-0 pointer-events-none z-20 overflow-hidden opacity-20"
            style={{ mixBlendMode: 'overlay' }}
          >
            <div 
              className="absolute left-0 right-0 h-1 bg-gradient-to-b from-transparent via-cyan-400 to-transparent"
              style={{ animation: 'scanline 3s linear infinite' }}
            />
          </div>

          {/* Digital noise overlay */}
          {isGlitching && (
            <div 
              className="absolute inset-0 pointer-events-none z-30"
              style={{
                background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 255, 255, 0.03) 2px, rgba(0, 255, 255, 0.03) 4px)',
                animation: 'digital-noise 0.3s steps(10) infinite',
              }}
            />
          )}

          {/* Glow effect */}
          <div 
            className="absolute inset-0 pointer-events-none z-0"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
              filter: 'blur(40px)',
              transform: 'scale(1.2)',
            }}
          />

          {/* Bottom fade — disimula el corte duro de la silueta */}
          <div
            className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none z-20"
            style={{
              background:
                'linear-gradient(to top, rgba(10, 14, 30, 0.95) 0%, rgba(10, 14, 30, 0.55) 40%, transparent 100%)',
            }}
          />

          {/* Corner glitch artifacts */}
          {isGlitching && (
            <>
              <motion.div
                className="absolute top-[10%] left-0 right-0 h-2 bg-cyan-500/30"
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: [0, 1, 0] }}
                transition={{ duration: 0.15 }}
              />
              <motion.div
                className="absolute top-[30%] left-0 right-0 h-1 bg-pink-500/20"
                initial={{ scaleX: 0, originX: 1 }}
                animate={{ scaleX: [0, 0.7, 0] }}
                transition={{ duration: 0.1, delay: 0.05 }}
              />
              <motion.div
                className="absolute top-[60%] left-0 right-0 h-3 bg-brand-yellow/20"
                initial={{ scaleX: 0, originX: 0.5 }}
                animate={{ scaleX: [0, 0.5, 0] }}
                transition={{ duration: 0.12 }}
              />
            </>
          )}
        </div>

        {/* Digital frame decoration */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Corner brackets */}
          <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-brand-yellow/40" />
          <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-brand-yellow/40" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-brand-yellow/40" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-brand-yellow/40" />
          
          {/* Scan lines texture */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              background: 'repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(255, 255, 255, 0.1) 1px, rgba(255, 255, 255, 0.1) 2px)',
            }}
          />
        </div>
      </div>
    </>
  );
}
